import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, Trash2, Building2, X, AlertCircle, CheckCircle, RotateCcw, Clock } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { apiGet, apiPost, apiDelete } from '../lib/api';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string);

const brandStyles = {
  primary: 'bg-[#2B8B3B] text-white hover:bg-[#236B2F]',
  secondary: 'bg-[#F5F5DC] text-[#2B8B3B] hover:bg-[#f0ebe1] hover:text-[#2B8B3B] border-[#2B8B3B]',
  accent: 'bg-[#F5F5DC]',
  text: 'text-[#2B8B3B]'
};

interface BillingAddress {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  status: string;
}

interface SavedCard {
  last4: string;
  brand: string;
  expMonth: number;
  expYear: number;
}

interface PaymentPageProps {
  onNavigate: (page: 'dashboard' | 'bookings' | 'payment' | 'account', scrollToDistro?: boolean) => void;
}

export default function PaymentPage({ onNavigate }: PaymentPageProps) {
  const { user } = useAuth();

  // Saved card state
  const [savedCard, setSavedCard] = useState<SavedCard | null>(null);
  const [cardLoading, setCardLoading] = useState(true);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeError, setRemoveError] = useState('');
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState('');

  const emptyAddress: BillingAddress = { street: '', city: '', state: '', zipCode: '', country: '' };
  const [billingAddress, setBillingAddress] = useState<BillingAddress>(emptyAddress);
  const [tempAddress, setTempAddress] = useState<BillingAddress>(emptyAddress);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressLoading, setAddressLoading] = useState(true);
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressError, setAddressError] = useState('');

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);

  // Fetch saved card on mount
  useEffect(() => {
    if (!user) return;
    setCardLoading(true);
    apiGet<{ savedCard: SavedCard | null }>('/api/payments/saved-card')
      .catch(() => ({ savedCard: null }))
      .then(({ savedCard: card }) => {
        setSavedCard(card);
        setCardLoading(false);
      });
  }, [user]);

  const handleRemoveCard = async () => {
    setRemoveLoading(true);
    setRemoveError('');
    try {
      await apiDelete('/api/payments/saved-card');
      setSavedCard(null);
      setRemoveConfirmOpen(false);
    } catch {
      setRemoveError('Failed to remove card. Please try again.');
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleOpenAddCard = async () => {
    setSetupError('');
    setSetupClientSecret(null);
    setSetupLoading(true);
    setAddCardOpen(true);
    try {
      const { clientSecret } = await apiPost<{ clientSecret: string; customerId: string }>('/api/payments/setup-intent', {});
      setSetupClientSecret(clientSecret);
    } catch {
      setSetupError('Unable to set up card saving. Please try again.');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleSetupComplete = async (card: SavedCard) => {
    setSavedCard(card);
    setAddCardOpen(false);
    setSetupClientSecret(null);
  };

  const brandLabel = (brand: string) => {
    const map: Record<string, string> = { visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover' };
    return map[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  useEffect(() => {
    if (!user) return;

    // Load billing address from profiles
    supabase
      .from('profiles')
      .select('city, state, billing_street, billing_zip, billing_country')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        const loaded: BillingAddress = {
          street: data?.billing_street ?? '',
          city: data?.city ?? '',
          state: data?.state ?? '',
          zipCode: data?.billing_zip ?? '',
          country: data?.billing_country ?? '',
        };
        setBillingAddress(loaded);
        setTempAddress(loaded);
        setAddressLoading(false);
      });

    // Load recent transactions from bookings joined with courses
    supabase
      .from('bookings')
      .select('id, slot_start, total_dollar_amount, status, courses(name)')
      .eq('player_id', user.id)
      .order('slot_start', { ascending: false })
      .limit(10)
      .then(({ data }) => {
        const rows: Transaction[] = (data ?? []).map((b: any) => ({
          id: b.id,
          date: b.slot_start,
          description: b.courses?.name ?? 'Unknown Course',
          amount: b.total_dollar_amount ?? 0,
          status: b.status,
        }));
        setTransactions(rows);
        setTransactionsLoading(false);
      });
  }, [user]);

  const handleSaveAddress = async () => {
    if (!user) return;
    setAddressSaving(true);
    setAddressError('');
    const { error } = await supabase
      .from('profiles')
      .update({
        billing_street: tempAddress.street,
        city: tempAddress.city,
        state: tempAddress.state,
        billing_zip: tempAddress.zipCode,
        billing_country: tempAddress.country,
      })
      .eq('id', user.id);
    setAddressSaving(false);
    if (error) {
      setAddressError('Failed to save. Please try again.');
      return;
    }
    setBillingAddress(tempAddress);
    setIsEditingAddress(false);
  };

  const handleCancelAddress = () => {
    setTempAddress(billingAddress);
    setAddressError('');
    setIsEditingAddress(false);
  };

  const statusBadge = (status: string) => {
    if (status === 'confirmed' || status === 'paid' || status === 'reserved') {
      return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-500 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Confirmed</Badge>;
    }
    if (status === 'requested') {
      return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-500 text-xs"><Clock className="w-3 h-3 mr-1" />Pending Request</Badge>;
    }
    if (status === 'refunded') {
      return <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300 text-xs"><RotateCcw className="w-3 h-3 mr-1" />Refunded</Badge>;
    }
    if (status === 'cancelled') {
      return <Badge variant="outline" className="bg-red-50 text-red-600 border-red-500 text-xs"><X className="w-3 h-3 mr-1" />Cancelled</Badge>;
    }
    return <Badge variant="outline" className="text-xs">{status}</Badge>;
  };

  const hasAddress = billingAddress.street || billingAddress.city || billingAddress.zipCode;

  return (
    <div className="min-h-screen bg-[#F5F5DC]">
      <Header onNavigate={onNavigate} />
      <div style={{ height: '64px' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-8">
          <h2 className={brandStyles.text}>Payment Information</h2>
          <p className="text-gray-600 mt-1">Manage your payment methods and billing information</p>
        </div>

        <div className="space-y-6">
          {/* Payment Methods */}
          <Card className="border-2 border-gray-100">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Payment Methods</CardTitle>
                  <CardDescription className="mt-1">Saved card for booking tee times</CardDescription>
                </div>
                {!cardLoading && !savedCard && (
                  <Button className={brandStyles.primary} onClick={handleOpenAddCard}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Card
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {cardLoading ? (
                <div className="text-sm text-gray-400 py-2">Loading…</div>
              ) : savedCard ? (
                <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {brandLabel(savedCard.brand)} ···· {savedCard.last4}
                      </p>
                      <p className="text-xs text-gray-500">
                        Expires {savedCard.expMonth}/{String(savedCard.expYear).slice(-2)}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => { setRemoveError(''); setRemoveConfirmOpen(true); }}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No payment method saved</p>
                  <p className="text-xs text-gray-400 mt-1">Save a card to speed up checkout</p>
                </div>
              )}
              {removeError && (
                <p className="mt-2 text-sm text-red-600">{removeError}</p>
              )}
            </CardContent>
          </Card>

          {/* Remove card confirmation dialog */}
          {removeConfirmOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="fixed inset-0 bg-black/50" onClick={() => !removeLoading && setRemoveConfirmOpen(false)} />
              <div className="relative bg-white rounded-xl shadow-lg p-6 w-full max-w-sm mx-4 z-10">
                <button
                  onClick={() => !removeLoading && setRemoveConfirmOpen(false)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                  disabled={removeLoading}
                >
                  <X className="w-4 h-4" />
                </button>
                <h3 className="text-base font-semibold mb-2">Remove this card?</h3>
                <p className="text-sm text-gray-600 mb-6">
                  You'll need to enter your card details at checkout.
                </p>
                {removeError && (
                  <div className="flex items-start gap-2 mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {removeError}
                  </div>
                )}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setRemoveConfirmOpen(false)}
                    disabled={removeLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    onClick={handleRemoveCard}
                    disabled={removeLoading}
                  >
                    {removeLoading ? 'Removing…' : 'Remove'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Add card modal */}
          {addCardOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="fixed inset-0 bg-black/50" onClick={() => { if (!setupLoading) { setAddCardOpen(false); setSetupClientSecret(null); } }} />
              <div className="relative bg-white rounded-xl shadow-lg p-6 w-full max-w-md mx-4 z-10">
                <button
                  onClick={() => { setAddCardOpen(false); setSetupClientSecret(null); }}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
                <h3 className="text-base font-semibold mb-4">Add a card</h3>
                {setupError && (
                  <div className="flex items-start gap-2 mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {setupError}
                  </div>
                )}
                {setupLoading && <div className="text-sm text-gray-400 py-4 text-center">Setting up secure card entry…</div>}
                {setupClientSecret && (
                  <Elements
                    stripe={stripePromise}
                    options={{ clientSecret: setupClientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: '#2B8B3B' } } }}
                  >
                    <AddCardForm
                      clientSecret={setupClientSecret}
                      onSuccess={handleSetupComplete}
                      onError={(msg) => setSetupError(msg)}
                    />
                  </Elements>
                )}
              </div>
            </div>
          )}

          {/* Billing Address */}
          <Card className="border-2 border-gray-100">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Billing Address</CardTitle>
                  <CardDescription className="mt-1">The address associated with your payment method</CardDescription>
                </div>
                {!isEditingAddress ? (
                  <Button
                    variant="outline"
                    className="rounded-lg"
                    onClick={() => setIsEditingAddress(true)}
                    disabled={addressLoading}
                  >
                    {hasAddress ? 'Edit' : 'Add Address'}
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" className="rounded-lg" onClick={handleCancelAddress} disabled={addressSaving}>
                      Cancel
                    </Button>
                    <Button className={brandStyles.primary} onClick={handleSaveAddress} disabled={addressSaving}>
                      {addressSaving ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {addressLoading ? (
                <div className="text-sm text-gray-400 py-2">Loading…</div>
              ) : isEditingAddress ? (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="billing-street">Street Address</Label>
                    <Input
                      id="billing-street"
                      type="text"
                      value={tempAddress.street}
                      onChange={(e) => setTempAddress({ ...tempAddress, street: e.target.value })}
                      className="mt-2"
                      placeholder="123 Main St"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="billing-city">City</Label>
                      <Input
                        id="billing-city"
                        type="text"
                        value={tempAddress.city}
                        onChange={(e) => setTempAddress({ ...tempAddress, city: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="billing-state">State</Label>
                      <Input
                        id="billing-state"
                        type="text"
                        value={tempAddress.state}
                        onChange={(e) => setTempAddress({ ...tempAddress, state: e.target.value })}
                        className="mt-2"
                        placeholder="OR"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="billing-zip">ZIP Code</Label>
                      <Input
                        id="billing-zip"
                        type="text"
                        value={tempAddress.zipCode}
                        onChange={(e) => setTempAddress({ ...tempAddress, zipCode: e.target.value })}
                        className="mt-2"
                        placeholder="97201"
                      />
                    </div>
                    <div>
                      <Label htmlFor="billing-country">Country</Label>
                      <Input
                        id="billing-country"
                        type="text"
                        value={tempAddress.country}
                        onChange={(e) => setTempAddress({ ...tempAddress, country: e.target.value })}
                        className="mt-2"
                        placeholder="United States"
                      />
                    </div>
                  </div>
                  {addressError && <p className="text-sm text-red-600">{addressError}</p>}
                </div>
              ) : hasAddress ? (
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <Building2 className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      {billingAddress.street && <p className="font-medium">{billingAddress.street}</p>}
                      {(billingAddress.city || billingAddress.state || billingAddress.zipCode) && (
                        <p className="text-gray-600">
                          {[billingAddress.city, billingAddress.state].filter(Boolean).join(', ')}
                          {billingAddress.zipCode ? ` ${billingAddress.zipCode}` : ''}
                        </p>
                      )}
                      {billingAddress.country && <p className="text-gray-600">{billingAddress.country}</p>}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No billing address saved.</p>
              )}
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card className="border-2 border-gray-100">
            <CardHeader>
              <CardTitle>Recent Transactions</CardTitle>
              <CardDescription className="mt-1">Your latest payment activity</CardDescription>
            </CardHeader>
            <CardContent>
              {transactionsLoading ? (
                <div className="text-sm text-gray-400 py-4">Loading…</div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No transactions yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((t) => (
                    <div key={t.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                      <div>
                        <p className="font-medium text-sm">{t.description}</p>
                        <p className="text-xs text-gray-600">
                          {new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">${Number(t.amount).toFixed(2)}</p>
                        {statusBadge(t.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ── AddCardForm — rendered inside <Elements clientSecret> in the add card modal ──

interface AddCardFormProps {
  clientSecret: string;
  onSuccess: (card: SavedCard) => void;
  onError: (msg: string) => void;
}

function AddCardForm({ clientSecret, onSuccess, onError }: AddCardFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (error) {
      onError(error.message ?? 'Unable to save card. Please try again.');
      setSubmitting(false);
      return;
    }

    if (setupIntent?.status === 'succeeded') {
      // Ask server to set this as default and return card display info
      try {
        const { savedCard } = await apiPost<{ savedCard: SavedCard | null }>('/api/payments/set-default-card', {});
        if (savedCard) {
          onSuccess(savedCard);
        } else {
          onError('Card saved but could not retrieve details. Please refresh.');
        }
      } catch {
        onError('Card saved but could not retrieve details. Please refresh.');
      }
    }

    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      <Button
        type="submit"
        className="w-full bg-[#2B8B3B] text-white hover:bg-[#236B2F]"
        disabled={!stripe || !elements || submitting}
      >
        {submitting ? 'Saving…' : 'Save Card'}
      </Button>
    </form>
  );
}
